import type { FC, ComponentProps } from 'react';
import React from 'react';
import type { GraphQLTaggedNode } from 'react-relay';
import { graphql, useFragment } from 'react-relay';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
import { xcss } from '@atlaskit/primitives/xcss';

import { ByLine } from '@confluence/content-header/entry-points/ByLine.ts';
import {
	ContentTitle,
	ContentTitleFromFragment,
} from '@confluence/content-header/entry-points/ContentTitle.ts';
import type { ContentTitle_space$key } from '@confluence/content-header/entry-points/ContentTitleSpaceFragmentType.ts';
import { TitleAlignmentType } from '@confluence/content-topper/entry-points/contentTopperConstants.ts';
import {
	getTitleContentProperty,
	useTitleContentPropertiesForPublishedPage,
} from '@confluence/content-topper/entry-points/useTitleContentPropertiesForPublishedPage.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { ApprovalBanner } from '@confluence/approvals/entry-points/ApprovalBanner.ts';

import { useViewPageParams } from './useViewPageParams.ts';
import type { ContentTitleWithByLine_content$key } from './__generated__/ContentTitleWithByLine_content.graphql.ts';

export const ContentTitleWithByLineFragment: GraphQLTaggedNode = graphql`
	fragment ContentTitleWithByLine_content on Content
	@argumentDefinitions(useNewContentTopper: { type: "Boolean!", defaultValue: false }) {
		...ContentTitle_content @arguments(useNewContentTopper: $useNewContentTopper)
		...ByLine_content @arguments(useNewContentTopper: $useNewContentTopper)
	}
`;

type ByLinePassThroughProps = Pick<
	ComponentProps<typeof ByLine>,
	'hasByLineContributors' | 'hasByLineExtensions'
>;

type Props = Partial<ByLinePassThroughProps> &
	ComponentProps<typeof ContentTitle> & {
		hasCoverPicture?: boolean;
		pageWidthType?: string;
		isEmbeddedPage?: boolean;
		shouldShowBylineReactions?: boolean;
		contentType?: string | null;
		spaceId?: string | null;
		contentSubType?: string | null;
		hasByLine?: boolean;
		contentFragmentRef?: ContentTitleWithByLine_content$key | null;
		titleSpaceFragmentRef?: ContentTitle_space$key | null;
	};

const BylineCenterStyles = xcss({
	justifyContent: 'center',
});

const ApprovalBannerStyles = xcss({
	marginBlockEnd: 'space.200',
});

export const ContentTitleWithByLine: FC<Props> = ({
	contentFragmentRef,
	titleSpaceFragmentRef,
	...props
}: Props) => {
	if (contentFragmentRef && fg('confluence_vp_title_byline_frag_dedup')) {
		return (
			<ContentTitleWithByLineFragmentReader
				{...props}
				contentFragmentRef={contentFragmentRef}
				titleSpaceFragmentRef={titleSpaceFragmentRef}
			/>
		);
	}

	return <ContentTitleWithByLineQuery {...props} />;
};

const ContentTitleWithByLineQuery: FC<Props> = ({
	contentId,
	spaceKey,
	banner,
	hasByLineContributors,
	hasByLineExtensions,
	headingContainer,
	hasCoverPicture,
	pageWidthType,
	isEmbeddedPage,
	shouldShowBylineReactions,
	contentType,
	contentSubType,
	spaceId,
	hasByLine = true,
}: Props) => {
	const { versionOverride, embeddedContentRender } = useViewPageParams();

	const { titleContentProperties } = useTitleContentPropertiesForPublishedPage({
		contentId,
	});

	const isBylineCenterStyles =
		titleContentProperties?.titleLayoutAlignment == TitleAlignmentType.CENTER;

	return (
		<ContentTitle
			banner={banner}
			contentId={contentId}
			spaceKey={spaceKey}
			headingContainer={headingContainer}
			hasCoverPicture={hasCoverPicture}
			pageWidthType={pageWidthType}
			isEmbeddedPage={isEmbeddedPage}
		>
			<Box
				xcss={[isBylineCenterStyles && BylineCenterStyles]}
				testId={
					isBylineCenterStyles ? 'centered-content-title-and-byline' : 'content-title-and-byline'
				}
			>
				{hasByLine && (
					<>
						<ByLine
							contentId={contentId}
							versionOverride={versionOverride}
							embeddedContentRender={embeddedContentRender}
							hasByLineContributors={hasByLineContributors}
							hasByLineExtensions={hasByLineExtensions}
							shouldShowBylineReactions={shouldShowBylineReactions}
							contentType={contentType}
							contentSubType={contentSubType}
							spaceId={spaceId}
						/>
						{expValEquals('confluence_approvals_eap', 'isEnabled', true) && (
							<ErrorBoundary attribution={Attribution.ADMIN_EXPERIENCE}>
								<Box xcss={ApprovalBannerStyles}>
									<ApprovalBanner />
								</Box>
							</ErrorBoundary>
						)}
					</>
				)}
			</Box>
		</ContentTitle>
	);
};

const ContentTitleWithByLineFragmentReader: FC<
	Props & {
		contentFragmentRef: ContentTitleWithByLine_content$key;
	}
> = ({
	contentFragmentRef,
	titleSpaceFragmentRef,
	contentId,
	spaceKey,
	banner,
	hasByLineContributors,
	hasByLineExtensions,
	headingContainer,
	hasCoverPicture,
	pageWidthType,
	isEmbeddedPage,
	shouldShowBylineReactions,
	contentType,
	contentSubType,
	spaceId,
	hasByLine = true,
}) => {
	const { versionOverride, embeddedContentRender } = useViewPageParams();
	const content = useFragment(ContentTitleWithByLineFragment, contentFragmentRef);
	const { titleContentProperties } = getTitleContentProperty({ content: { nodes: [content] } });

	const isBylineCenterStyles =
		titleContentProperties?.titleLayoutAlignment == TitleAlignmentType.CENTER;

	return (
		<ContentTitleFromFragment
			banner={banner}
			contentId={contentId}
			spaceKey={spaceKey}
			headingContainer={headingContainer}
			hasCoverPicture={hasCoverPicture}
			pageWidthType={pageWidthType}
			isEmbeddedPage={isEmbeddedPage}
			contentFragmentRef={content}
			spaceFragmentRef={titleSpaceFragmentRef}
		>
			<Box
				xcss={[isBylineCenterStyles && BylineCenterStyles]}
				testId={
					isBylineCenterStyles ? 'centered-content-title-and-byline' : 'content-title-and-byline'
				}
			>
				{hasByLine && (
					<>
						<ByLine
							contentId={contentId}
							versionOverride={versionOverride}
							embeddedContentRender={embeddedContentRender}
							hasByLineContributors={hasByLineContributors}
							hasByLineExtensions={hasByLineExtensions}
							shouldShowBylineReactions={shouldShowBylineReactions}
							contentType={contentType}
							contentSubType={contentSubType}
							spaceId={spaceId}
						/>
						{expValEquals('confluence_approvals_eap', 'isEnabled', true) && (
							<ErrorBoundary attribution={Attribution.ADMIN_EXPERIENCE}>
								<Box xcss={ApprovalBannerStyles}>
									<ApprovalBanner />
								</Box>
							</ErrorBoundary>
						)}
					</>
				)}
			</Box>
		</ContentTitleFromFragment>
	);
};
